/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * AI Assistant seat assignment.
 *
 * Same pattern as CallCenterSeats: the organisation buys N seats from the
 * platform, and a system administrator decides which N users get them. A
 * user without a seat never sees the AI features.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Card,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";

const MODULE = "AI_ASSISTANT";
const CARD_SX = {
  p: 3,
  border: `1px solid ${colors.callCenter.cardBorder}`,
  boxShadow: "none",
  borderRadius: "12px",
};

export function displayName(u) {
  const parts = [u?.first_name, u?.last_name].filter(Boolean).join(" ").trim();
  return parts || u?.username || u?.email || `User ${u?.id}`;
}

export default function AiAssistantSeats({ users, embedded }) {
  const { axiosInstance, user: currentUser, isAuthReady, isAuthenticated } = useAuth();

  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingUserId, setSavingUserId] = useState(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated) return;
    try {
      const res = await axiosInstance.get(`/license/module-seats/${MODULE}`);
      setSeats(res.data);
    } catch {
      setSeats(null);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAuthReady, isAuthenticated]);

  useEffect(() => {
    load();
  }, [load]);

  const assigned = useMemo(() => new Set(seats?.assigned_user_ids || []), [seats]);
  const isSystemAdmin = String(currentUser?.role || "").toLowerCase() === "system_admin";

  const toggle = async (targetUser, next) => {
    setSavingUserId(targetUser.id);
    setError("");
    try {
      const res = await axiosInstance.post(
        `/license/module-seats/${MODULE}/${next ? "assign" : "revoke"}`,
        { user_id: targetUser.id }
      );
      setSeats(res.data.seats);
      setToast(
        next
          ? `${displayName(targetUser)} can now use the AI Assistant.`
          : `${displayName(targetUser)}'s AI Assistant seat was released.`
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not change that seat."));
      // The server is the authority on seat counts; re-read rather than guess.
      load();
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </MDBox>
    );
  }

  // Inside User Management, next to the Call Center and WhatsApp panels: stay
  // out of the way when the add-on was never bought.
  if ((!seats || !seats.purchased) && embedded) return null;

  if (!seats || !seats.purchased) {
    return (
      <Card sx={CARD_SX}>
        <MDTypography variant="h6">No AI Assistant seats yet</MDTypography>
        <MDTypography variant="body2" color="text" mt={0.5}>
          Seats come with the AI Assistant add-on. Ask your PearlCRM account manager how many your
          organisation needs.
        </MDTypography>
      </Card>
    );
  }

  const term = search.trim().toLowerCase();
  const assignable = (users || [])
    .filter((u) => u.is_active !== false && !u.is_support_user)
    .filter(
      (u) =>
        !term ||
        displayName(u).toLowerCase().includes(term) ||
        String(u.email || "").toLowerCase().includes(term)
    );
  const pct = seats.total_seats ? Math.min(100, (seats.used_seats / seats.total_seats) * 100) : 0;
  const full = seats.seats_remaining <= 0;

  return (
    <Card sx={embedded ? { ...CARD_SX, mt: 3 } : CARD_SX}>
      <MDBox display="flex" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
        <MDBox>
          <MDTypography variant="h6">{embedded ? "AI Assistant seats" : "Seats"}</MDTypography>
          <MDTypography variant="body2" color="text">
            Only users with a seat see the AI Assistant and can use it.
          </MDTypography>
        </MDBox>
        <MDTypography variant="button" fontWeight="bold">
          {seats.used_seats} of {seats.total_seats} assigned
        </MDTypography>
      </MDBox>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={full ? "warning" : "info"}
        sx={{ height: 6, borderRadius: 3, my: 1.5 }}
      />

      {full && (
        <Alert severity="info" sx={{ mb: 2, py: 0.25 }}>
          Every seat is assigned. Release one below, or contact your account manager for more seats.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.25 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <TextField
        size="small"
        placeholder="Search users"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 1, maxWidth: 320 }}
        inputProps={{ "aria-label": "Search users" }}
      />

      <MDBox display="flex" flexDirection="column">
        {assignable.map((u) => {
          const has = assigned.has(u.id);
          const blocked = !has && full;
          return (
            <MDBox
              key={u.id}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              py={0.75}
              sx={{ borderBottom: `1px solid ${colors.callCenter.surface}` }}
            >
              <MDBox minWidth={0}>
                <MDTypography variant="button" color="dark" display="block" noWrap>
                  {displayName(u)}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {u.email || u.username}
                </MDTypography>
              </MDBox>
              <Tooltip
                title={
                  !isSystemAdmin
                    ? "Only a system administrator can change seats"
                    : blocked
                    ? "No AI Assistant seats are free"
                    : has
                    ? "Release this seat"
                    : "Give this user an AI Assistant seat"
                }
              >
                <span>
                  <Switch
                    checked={has}
                    disabled={!isSystemAdmin || blocked || savingUserId === u.id}
                    onChange={(e) => toggle(u, e.target.checked)}
                    inputProps={{ "aria-label": `AI Assistant seat for ${displayName(u)}` }}
                  />
                </span>
              </Tooltip>
            </MDBox>
          );
        })}
        {assignable.length === 0 && (
          <MDTypography variant="button" color="text" py={1}>
            {term ? "No users match that search." : "No active users to assign."}
          </MDTypography>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3500}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>
    </Card>
  );
}

AiAssistantSeats.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object),
  embedded: PropTypes.bool,
};

AiAssistantSeats.defaultProps = { users: [], embedded: false };
